import React, { useState } from 'react'
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Badge,
} from '@mui/material'
import {
  Security as SecurityIcon,
  ExitToApp as LogoutIcon,
  RequestPage as RequestIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Permission, PermissionCategory, PermissionLevel } from '../../types/navigation'

interface UserProfileDropdownProps {
  user: {
    id: string
    email: string
    name?: string
    role: string
    permissions?: Record<string, boolean>
    is_superuser?: boolean
    avatar?: string
  }
  onLogout: () => void
  onRequestAccess?: (permissionId: string, reason: string) => void
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  user,
  onLogout,
  onRequestAccess
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [requestReason, setRequestReason] = useState('')
  const [selectedPermission, setSelectedPermission] = useState('')

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRequestAccess = () => {
    setRequestDialogOpen(true)
    handleClose()
  }

  const handleSubmitRequest = () => {
    if (onRequestAccess && selectedPermission && requestReason.trim()) {
      onRequestAccess(selectedPermission, requestReason.trim())
      setRequestDialogOpen(false)
      setRequestReason('')
      setSelectedPermission('')
    }
  }

  const handleCancelRequest = () => {
    setRequestDialogOpen(false)
    setRequestReason('')
    setSelectedPermission('')
  }

  // Convert permission dictionary to list for display
  const userPermissions = Object.entries(user.permissions || {}).map(([id, active]) => {
    if (!active) return null
    const [category, level] = id.split('.')
    return {
      id,
      name: id.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      category: (category || 'general') as PermissionCategory,
      level: (level || 'access') as PermissionLevel,
      description: `Access to ${category} ${level}`
    }
  }).filter(Boolean) as Permission[]

  const groupedPermissions = userPermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = []
    }
    acc[permission.category].push(permission)
    return acc
  }, {} as Record<string, Permission[]>)

  const getPermissionLevelColor = (level: PermissionLevel) => {
    switch (level) {
      case PermissionLevel.READ:
        return 'info'
      case PermissionLevel.WRITE:
        return 'warning'
      case PermissionLevel.DELETE:
        return 'error'
      case PermissionLevel.ADMIN:
        return 'success'
      default:
        return 'default'
    }
  }

  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case PermissionLevel.READ:
        return <CheckIcon fontSize="small" />
      case PermissionLevel.WRITE:
        return <CheckIcon fontSize="small" />
      case PermissionLevel.DELETE:
        return <WarningIcon fontSize="small" />
      case PermissionLevel.ADMIN:
        return <SecurityIcon fontSize="small" />
      default:
        return <CheckIcon fontSize="small" />
    }
  }

  const isPermissionExpiring = (permission: Permission) => {
    if (!permission.expiryDate) return false
    const expiryDate = new Date(permission.expiryDate)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0
  }

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }
    if (email) {
      return email.substring(0, 2).toUpperCase()
    }
    return 'U'
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {getInitials(user.name, user.email)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 320,
            maxWidth: 400,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', mr: 1.5 }}>
              {getInitials(user.name, user.email)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" noWrap>
                {user.name || user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user.email}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Permissions */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <SecurityIcon fontSize="small" sx={{ mr: 0.5 }} />
            Your Permissions
          </Typography>

          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {Object.entries(groupedPermissions).map(([category, permissions]) => (
              <Box key={category} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {permissions.map((permission) => (
                    <ListItem key={permission.id} sx={{ px: 0, py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {isPermissionExpiring(permission) ? (
                          <Badge badgeContent="!" color="warning" variant="dot">
                            {getPermissionIcon(permission.level)}
                          </Badge>
                        ) : (
                          getPermissionIcon(permission.level)
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption">
                              {permission.name}
                            </Typography>
                            <Chip
                              label={permission.level}
                              size="small"
                              color={getPermissionLevelColor(permission.level) as any}
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.625rem' }}
                            />
                            {permission.expiryDate && (
                              <Chip
                                icon={<ScheduleIcon />}
                                label={new Date(permission.expiryDate).toLocaleDateString()}
                                size="small"
                                color={isPermissionExpiring(permission) ? 'warning' : 'default'}
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.625rem' }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Actions */}
        <MenuItem onClick={() => { handleClose(); /* Navigate to profile */ }}>
          <ListItemIcon>
            <Avatar sx={{ width: 24, height: 24 }}>
              {getInitials(user.name, user.email)}
            </Avatar>
          </ListItemIcon>
          My Profile
        </MenuItem>

        <MenuItem onClick={handleRequestAccess}>
          <ListItemIcon>
            <RequestIcon fontSize="small" />
          </ListItemIcon>
          Request Additional Access
        </MenuItem>

        <Divider />

        <MenuItem onClick={onLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Request Access Dialog */}
      <Dialog open={requestDialogOpen} onClose={handleCancelRequest} maxWidth="sm" fullWidth>
        <DialogTitle>Request Additional Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Request additional permissions from your administrator. Please provide a reason for your request.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Permission Needed"
            select
            fullWidth
            variant="outlined"
            value={selectedPermission}
            onChange={(e) => setSelectedPermission(e.target.value)}
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">Select a permission...</option>
            <option value="customers.write">Manage Customers</option>
            <option value="products.delete">Delete Products</option>
            <option value="orders.admin">Full Order Management</option>
            <option value="configuration.admin">System Configuration</option>
            <option value="analytics.admin">Advanced Analytics</option>
          </TextField>
          <TextField
            margin="dense"
            label="Reason for Request"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            placeholder="Please explain why you need this permission..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRequest}>Cancel</Button>
          <Button
            onClick={handleSubmitRequest}
            variant="contained"
            disabled={!selectedPermission || !requestReason.trim()}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserProfileDropdown